import React, { useState, useEffect } from 'react'
import { UsersIcon, AcademicCapIcon, CurrencyYenIcon, ExclamationTriangleIcon, CalendarDaysIcon, ChartBarIcon } from '@heroicons/react/24/solid'
import { StatCard } from './StatCard'
import { BarChart } from './BarChart'
import { TrendChart } from './TrendChart'
import { dashboardApi } from '../../services/adminApi'

interface DashboardStats {
  overview: {
    totalUsers: number
    activeUsers: number
    totalTeachers: number
    activeTeachers: number
    totalStudents: number
    pendingTeachers: number
    totalCourses: number
    activeCourses: number
  }
  bookings: {
    total: number
    completed: number
    pending: number
  }
  payments: {
    totalRevenue: number
    todayRevenue: number
    weekRevenue: number
    monthRevenue: number
    pendingCount: number
    totalCount: number
  }
  complaints: {
    total: number
    pending: number
  }
  growth: {
    todayUsers: number
    weekTeachers: number
    monthStudents: number
  }
  charts: {
    courseCategories: Array<{ language: string; count: number }>
    userTypeDistribution: Array<{ user_type: string; count: number }>
    userRegistrationTrend: Array<{ date: string; count: number }>
    bookingTrend: Array<{ date: string; count: number }>
  }
}

export const DashboardContent: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<DashboardStats | null>(null)

  const loadDashboardStats = async () => {
    try {
      setLoading(true)
      const response = await dashboardApi.getStats()
      if (response.success) {
        setStats(response.data)
      }
    } catch (error) {
      console.error('加载统计数据失败:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadDashboardStats()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">无法加载统计数据</p>
      </div>
    )
  }

  // 格式化金额
  const formatCurrency = (amount: number) => {
    return `¥${amount.toLocaleString()}`
  }

  return (
    <div>
      {/* 页面标题 */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">管理仪表板</h1>
        <p className="mt-2 text-gray-600">欢迎回到 TalkNect 管理端 - 实时数据概览</p>
      </div>

      {/* 主要统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="总用户数"
          value={stats.overview.totalUsers.toLocaleString()}
          subtitle={`活跃: ${stats.overview.activeUsers.toLocaleString()}`}
          icon={<UsersIcon className="w-6 h-6 text-blue-600" />}
          color="bg-blue-100"
          trend={{
            value: stats.growth?.todayUsers || 0,
            label: "今日新增",
            direction: "up"
          }}
        />

        <StatCard
          title="认证教师"
          value={(stats.overview.activeTeachers || 0).toLocaleString()}
          subtitle={`待审核: ${stats.overview.pendingTeachers || 0}`}
          icon={<AcademicCapIcon className="w-6 h-6 text-green-600" />}
          color="bg-green-100"
          trend={{
            value: stats.growth?.weekTeachers || 0,
            label: "本周新增",
            direction: "up"
          }}
        />

        <StatCard
          title="总收入"
          value={formatCurrency(stats.payments?.totalRevenue || 0)}
          subtitle={`今日: ${formatCurrency(stats.payments?.todayRevenue || 0)}`}
          icon={<CurrencyYenIcon className="w-6 h-6 text-yellow-600" />}
          color="bg-yellow-100"
          trend={{
            value: stats.payments?.weekRevenue || 0,
            label: "本周收入",
            direction: "up"
          }}
        />

        <StatCard
          title="待处理事项"
          value={(stats.complaints?.pending || 0) + (stats.payments?.pendingCount || 0) + (stats.overview?.pendingTeachers || 0)}
          subtitle={`投诉: ${stats.complaints?.pending || 0} | 支付: ${stats.payments?.pendingCount || 0}`}
          icon={<ExclamationTriangleIcon className="w-6 h-6 text-red-600" />}
          color="bg-red-100"
        />
      </div>

      {/* 次要统计 */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <StatCard
          title="总课程数"
          value={stats.overview?.totalCourses || 0}
          icon={<CalendarDaysIcon className="w-6 h-6 text-purple-600" />}
          color="bg-purple-100"
        />

        <StatCard
          title="学生总数"
          value={(stats.overview?.totalStudents || 0).toLocaleString()}
          trend={{
            value: stats.growth?.monthStudents || 0,
            label: "本月新增",
            direction: "up"
          }}
          icon={<UsersIcon className="w-6 h-6 text-indigo-600" />}
          color="bg-indigo-100"
        />

        <StatCard
          title="课程预约"
          value={stats.bookings?.total || 0}
          subtitle={`完成: ${stats.bookings?.completed || 0}`}
          icon={<CalendarDaysIcon className="w-6 h-6 text-orange-600" />}
          color="bg-orange-100"
        />

        <StatCard
          title="月收入"
          value={formatCurrency(stats.payments?.monthRevenue || 0)}
          icon={<ChartBarIcon className="w-6 h-6 text-teal-600" />}
          color="bg-teal-100"
        />

        <StatCard
          title="支付总数"
          value={stats.payments?.totalCount || 0}
          subtitle={`待处理: ${stats.payments?.pendingCount || 0}`}
          icon={<CurrencyYenIcon className="w-6 h-6 text-pink-600" />}
          color="bg-pink-100"
        />

        <StatCard
          title="总投诉数"
          value={stats.complaints?.total || 0}
          subtitle={`待处理: ${stats.complaints?.pending || 0}`}
          icon={<ExclamationTriangleIcon className="w-6 h-6 text-red-600" />}
          color="bg-red-100"
        />
      </div>

      {/* 图表区域 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* 课程分类分布 */}
        <BarChart
          title="课程分类分布"
          data={(stats.charts?.courseCategories || []).map(item => ({
            label: item.language || '未知',
            value: item.count,
            color: 'bg-purple-600'
          }))}
        />

        {/* 用户类型分布 */}
        <BarChart
          title="用户类型分布"
          data={(stats.charts?.userTypeDistribution || []).map(item => ({
            label: item.user_type === 'student' ? '学生' :
                  item.user_type === 'teacher' ? '教师' : '管理员',
            value: item.count,
            color: item.user_type === 'student' ? 'bg-blue-600' :
                  item.user_type === 'teacher' ? 'bg-green-600' : 'bg-purple-600'
          }))}
        />
      </div>

      {/* 趋势图表 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 用户注册趋势 */}
        <TrendChart
          title="最近7天用户注册趋势"
          data={(stats.charts?.userRegistrationTrend || []).map((p: any) => ({
            date: p.date,
            value: p.count
          }))}
          color="rgb(59, 130, 246)"
        />

        {/* 课程预约趋势 */}
        <TrendChart
          title="最近7天课程预约趋势"
          data={(stats.charts?.bookingTrend || []).map((p: any) => ({
            date: p.date,
            value: p.count
          }))}
          color="rgb(34, 197, 94)"
        />
      </div>

      {/* 快速操作区域 */}
      <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">快速操作</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button className="flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            <AcademicCapIcon className="w-6 h-6 mr-2" />
            教师审核
          </button>
          <button className="flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
            <UsersIcon className="w-6 h-6 mr-2" />
            用户管理
          </button>
          <button className="flex items-center justify-center px-4 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors">
            <CurrencyYenIcon className="w-6 h-6 mr-2" />
            支付管理
          </button>
          <button className="flex items-center justify-center px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
            <ExclamationTriangleIcon className="w-6 h-6 mr-2" />
            投诉处理
          </button>
        </div>
      </div>
    </div>
  )
}
